import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ContactLogo from './shared/ContactLogo';

const HOME_AVATARS = [
  'https://i.pravatar.cc/64?img=47',
  'https://i.pravatar.cc/64?img=32',
  'https://i.pravatar.cc/64?img=5',
];

const API = '/api';

const CHAT_LIMIT = 10;
const CHAT_WINDOW_MS = 24 * 60 * 60 * 1000;

const getChatUsage = () => {
  try {
    const raw = localStorage.getItem('cb_usage');
    if (!raw) return { count: 0, resetAt: Date.now() + CHAT_WINDOW_MS };
    return JSON.parse(raw);
  } catch { return { count: 0, resetAt: Date.now() + CHAT_WINDOW_MS }; }
};

const checkLimitReached = () => {
  const u = getChatUsage();
  return Date.now() <= u.resetAt && u.count >= CHAT_LIMIT;
};

const incrementUsage = () => {
  const u = getChatUsage();
  const now = Date.now();
  const next = now > u.resetAt
    ? { count: 1, resetAt: now + CHAT_WINDOW_MS }
    : { count: u.count + 1, resetAt: u.resetAt };
  localStorage.setItem('cb_usage', JSON.stringify(next));
};

const ChatBot = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState('home');
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const messagesEndRef = useRef(null);

  const isPublicProfilePage = location.pathname.startsWith('/pages/');
  const isHomepage = location.pathname === '/';
  // /comprar has its own fixed bottom bar on mobile (price + pay button),
  // so the chat bubble needs to sit above it there instead of at its
  // usual bottom offset, or the two would overlap in the same corner.
  const isComprarPage = location.pathname === '/comprar';
  const [heroReady, setHeroReady] = useState(!isHomepage || window.__nxHeroReady === true);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => { scrollToBottom(); }, [messages]);

  useEffect(() => {
    if (!isHomepage) { setHeroReady(true); return; }
    if (window.__nxHeroReady) { setHeroReady(true); return; }
    setHeroReady(false);
    const onHeroReady = () => setHeroReady(true);
    window.addEventListener('nx:hero-ready', onHeroReady);
    return () => window.removeEventListener('nx:hero-ready', onHeroReady);
  }, [isHomepage]);

  useEffect(() => {
    if (isOpen) setView('home');
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && !sessionId) {
      setLimitReached(checkLimitReached());
      const newSessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      setSessionId(newSessionId);
      setMessages([{
        role: 'assistant',
        content: 'Hola, soy Laura. Estoy aquí para ayudarte con cualquier duda sobre Contact y también para guiarte en el uso de la plataforma. ¿En qué te puedo ayudar?',
        timestamp: new Date()
      }]);
    }
  }, [isOpen, sessionId]);

  if (isPublicProfilePage || !heroReady) return null;

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading || limitReached) return;

    if (checkLimitReached()) {
      setLimitReached(true);
      setMessages(prev => [...prev,
        { role: 'user', content: inputMessage, timestamp: new Date() },
        { role: 'assistant', content: 'Has alcanzado el límite de 10 consultas disponibles por hoy. ¡Gracias por tu interés en Contact! Puedo ayudarte de estas otras formas:', timestamp: new Date(), isLimit: true }
      ]);
      setInputMessage('');
      return;
    }

    incrementUsage();
    const userMessage = { role: 'user', content: inputMessage, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/chatbot/send-message`, {
        session_id: sessionId,
        message: inputMessage
      });
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date(response.data.timestamp)
      }]);
      setSessionId(response.data.session_id);
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Disculpa, tuve un pequeño problema. ¿Podrías intentar de nuevo?',
        timestamp: new Date()
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <style>{`
        .cb-btn{position:fixed;bottom:24px;right:24px;width:50px;height:50px;border-radius:50%;background:#0F172A;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;z-index:50;transition:transform .15s,box-shadow .15s;box-shadow:0 10px 28px rgba(15,23,42,.24),0 2px 8px rgba(15,23,42,.14)}
        .cb-btn:hover{transform:scale(1.06) translateY(-1px);box-shadow:0 14px 34px rgba(15,23,42,.3),0 3px 10px rgba(15,23,42,.18)}
        .cb-btn svg{color:#fff;width:21px;height:21px}

        .cb-window{position:fixed;bottom:24px;right:24px;width:360px;max-width:calc(100vw - 32px);height:520px;max-height:85vh;background:#fff;border:1px solid #E5E5E3;border-radius:20px;box-shadow:0 8px 40px rgba(0,0,0,.10);z-index:50;display:flex;flex-direction:column;overflow:hidden;animation:cbSlideUp .2s cubic-bezier(.16,1,.3,1)}
        @keyframes cbSlideUp{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}

        .cb-header{display:flex;align-items:center;justify-content:space-between;padding:16px 18px;border-bottom:1px solid #F0F0EE;flex-shrink:0}
        .cb-header-left{display:flex;align-items:center;gap:11px}
        .cb-avatar-wrap{position:relative}
        .cb-avatar{width:36px;height:36px;border-radius:50%;flex-shrink:0;display:flex;align-items:center;justify-content:center;background:#0F172A;box-shadow:0 0 0 2px #fff,0 0 0 3.5px rgba(34,197,94,.35)}
        .cb-avatar svg{width:19px;height:19px;color:#fff}
        .cb-name{font-size:14px;font-weight:600;color:#111;line-height:1.2}
        .cb-status{font-size:11px;color:#9CA3AF}
        .cb-close{width:28px;height:28px;border-radius:8px;background:none;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;color:#9CA3AF;transition:background .15s,color .15s}
        .cb-close:hover{background:#F4F4F5;color:#111}

        .cb-home-header{flex-shrink:0;padding:16px 18px 20px;background:linear-gradient(135deg,#E3F1FD 0%,#BFE0FA 45%,#8CC5F0 100%)}
        .cb-home-header-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:24px}
        .cb-home-top-right{display:flex;align-items:center}
        .cb-home-avatars{display:flex}
        .cb-home-avatars img{width:30px;height:30px;border-radius:50%;object-fit:cover;border:2px solid #fff;margin-left:-9px;background:#fff}
        .cb-home-avatars img:first-child{margin-left:0}
        .cb-home-close{background:rgba(255,255,255,.55);color:#1F2937;margin-left:10px}
        .cb-home-close:hover{background:rgba(255,255,255,.85);color:#111}

        .cb-home{flex:1;min-height:0;overflow-y:auto;padding:18px 16px 10px;display:flex;flex-direction:column}
        .cb-greeting-hi{font-size:22px;font-weight:700;color:#111;line-height:1.2;margin-bottom:4px}
        .cb-greeting-sub{font-size:14px;color:#3A4A5A}
        .cb-actions{display:flex;flex-direction:column;gap:8px}
        .cb-action{display:flex;align-items:center;gap:12px;width:100%;padding:12px 14px;background:#fff;border:1px solid #F0F0EE;border-radius:14px;text-decoration:none;cursor:pointer;font-family:inherit;text-align:left;transition:border-color .15s,transform .15s}
        .cb-action:hover{border-color:#E5E5E3;transform:translateY(-1px)}
        .cb-action-icon{width:34px;height:34px;border-radius:9px;background:#F5F5F7;display:flex;align-items:center;justify-content:center;flex-shrink:0;color:#0F172A}
        .cb-action-icon svg{width:16px;height:16px}
        .cb-action-label{flex:1;font-size:13.5px;font-weight:600;color:#111}
        .cb-action-chevron{color:#C4C4C2;font-size:17px;line-height:1;flex-shrink:0}

        .cb-tabbar{display:flex;border-top:1px solid #F0F0EE;flex-shrink:0}
        .cb-tab{flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;padding:9px 0 10px;background:none;border:none;cursor:pointer;color:#9CA3AF;font-family:inherit;transition:color .15s}
        .cb-tab svg{width:19px;height:19px}
        .cb-tab span{font-size:10.5px;font-weight:600}
        .cb-tab--active{color:#0F172A}

        .cb-messages{flex:1;min-height:0;overflow-y:auto;padding:16px 14px;display:flex;flex-direction:column;gap:10px;background:#fff}
        .cb-messages::-webkit-scrollbar{width:3px}
        .cb-messages::-webkit-scrollbar-track{background:transparent}
        .cb-messages::-webkit-scrollbar-thumb{background:#E5E5E3;border-radius:99px}

        .cb-row{display:flex}
        .cb-row--user{justify-content:flex-end}
        .cb-row--bot{justify-content:flex-start}
        .cb-bubble{max-width:80%;padding:10px 14px;border-radius:18px;font-size:13px;line-height:1.55;white-space:pre-wrap}
        .cb-bubble--user{background:#0F172A;color:#fff;border-bottom-right-radius:5px;box-shadow:0 3px 10px rgba(15,23,42,.18)}
        .cb-bubble--bot{background:#F5F5F7;color:#111;border-bottom-left-radius:5px}

        .cb-cta-group{display:flex;flex-direction:column;gap:7px;margin-top:8px;max-width:80%}
        .cb-cta{display:block;width:100%;text-align:center;padding:10px 16px;background:#0F172A;border:none;border-radius:999px;font-size:12.5px;font-weight:600;color:#fff;cursor:pointer;transition:opacity .15s,transform .15s;text-decoration:none;font-family:inherit}
        .cb-cta:hover{opacity:.86;transform:translateY(-1px)}

        .cb-typing{display:flex;align-items:center;gap:4px;padding:10px 13px;background:#F5F5F7;border-radius:18px;border-bottom-left-radius:5px;width:fit-content}
        .cb-dot-anim{width:6px;height:6px;border-radius:50%;background:#9CA3AF;animation:cbDot .9s ease-in-out infinite}
        @keyframes cbDot{0%,80%,100%{transform:scale(.7);opacity:.4}40%{transform:scale(1);opacity:1}}

        .cb-input-wrap{border-top:1px solid #F0F0EE;padding:12px 14px;flex-shrink:0}
        .cb-input-pill{display:flex;align-items:center;gap:6px;background:#F5F5F7;border:1px solid transparent;border-radius:999px;padding:4px 4px 4px 16px;transition:border-color .15s,background .15s}
        .cb-input-pill:focus-within{border-color:#0F172A;background:#fff}
        .cb-input{flex:1;min-width:0;border:none;padding:8px 0;font-size:13px;color:#111;background:transparent;outline:none;font-family:inherit}
        .cb-input::placeholder{color:#B5B5B3}
        .cb-send{width:32px;height:32px;border-radius:50%;background:#0F172A;border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:opacity .15s,transform .15s}
        .cb-send:hover:not(:disabled){opacity:.85;transform:scale(1.05)}
        .cb-send:disabled{opacity:.3;cursor:not-allowed}
        .cb-send svg{width:14px;height:14px;color:#fff}

        .cb-limit-bar{border-top:1px solid #F0F0EE;padding:12px 14px;text-align:center;flex-shrink:0}
        .cb-limit-txt{font-size:12px;color:#9CA3AF}

        @media(max-width:768px){
          .cb-input{font-size:16px}
        }
        @media(max-width:480px){
          .cb-window{bottom:16px;right:16px;left:16px;width:auto}
          .cb-btn{bottom:16px;right:16px}
          .cb-btn--raised{bottom:88px}
          .cb-window--raised{bottom:88px}
        }
      `}</style>

      {/* Botón flotante */}
      {!isOpen && (
        <button className={`cb-btn${isComprarPage ? ' cb-btn--raised' : ''}`} onClick={() => setIsOpen(true)} aria-label="Abrir chat">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
          </svg>
        </button>
      )}

      {/* Ventana de chat */}
      {isOpen && (
        <div className={`cb-window${isComprarPage ? ' cb-window--raised' : ''}`}>

          {/* Header */}
          {view === 'home' ? (
            <div className="cb-home-header">
              <div className="cb-home-header-top">
                <ContactLogo />
                <div className="cb-home-top-right">
                  <div className="cb-home-avatars">
                    {HOME_AVATARS.map((src, i) => (
                      <img key={i} src={src} alt="" />
                    ))}
                  </div>
                  <button className="cb-close cb-home-close" onClick={() => setIsOpen(false)} aria-label="Cerrar">
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="cb-greeting">
                <div className="cb-greeting-hi">Hola 👋</div>
                <div className="cb-greeting-sub">¿En qué podemos ayudarte?</div>
              </div>
            </div>
          ) : (
          <div className="cb-header">
            <div className="cb-header-left">
              <div className="cb-avatar-wrap">
                <div className="cb-avatar">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="8" r="4"/>
                    <path d="M4 21v-1a7 7 0 0114 0v1"/>
                  </svg>
                </div>
              </div>
              <div>
                <div className="cb-name">Laura</div>
                <div className="cb-status">En línea</div>
              </div>
            </div>
            <button className="cb-close" onClick={() => setIsOpen(false)} aria-label="Cerrar">
              <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          )}

          {/* Inicio */}
          {view === 'home' && (
            <div className="cb-home">
              <div className="cb-actions">
                <a href="mailto:ventas@contactnetwork.cl" className="cb-action">
                  <span className="cb-action-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
                    </svg>
                  </span>
                  <span className="cb-action-label">Cotizar</span>
                  <span className="cb-action-chevron">›</span>
                </a>
                <button type="button" onClick={() => navigate('/agenda-demo')} className="cb-action">
                  <span className="cb-action-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
                    </svg>
                  </span>
                  <span className="cb-action-label">Agendar una demo</span>
                  <span className="cb-action-chevron">›</span>
                </button>
                <button type="button" onClick={() => setView('messages')} className="cb-action">
                  <span className="cb-action-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                    </svg>
                  </span>
                  <span className="cb-action-label">Hablar con Laura</span>
                  <span className="cb-action-chevron">›</span>
                </button>
              </div>
            </div>
          )}

          {/* Mensajes */}
          {view === 'messages' && (
            <>
              <div className="cb-messages">
                {messages.map((msg, i) => (
                  <div key={i}>
                    <div className={`cb-row ${msg.role === 'user' ? 'cb-row--user' : 'cb-row--bot'}`}>
                      <div className={`cb-bubble ${msg.role === 'user' ? 'cb-bubble--user' : 'cb-bubble--bot'}`}>
                        {msg.content}
                      </div>
                    </div>
                    {msg.isLimit && (
                      <div className="cb-cta-group">
                        <a href="mailto:ventas@contactnetwork.cl" className="cb-cta">Cotizar</a>
                        <button onClick={() => navigate('/agenda-demo')} className="cb-cta">Agendar una demo</button>
                        <a href="mailto:ventas@contactnetwork.cl" className="cb-cta">Contactar al equipo</a>
                      </div>
                    )}
                  </div>
                ))}

                {isLoading && (
                  <div className="cb-row cb-row--bot">
                    <div className="cb-typing">
                      <div className="cb-dot-anim" style={{animationDelay:'0ms'}}/>
                      <div className="cb-dot-anim" style={{animationDelay:'160ms'}}/>
                      <div className="cb-dot-anim" style={{animationDelay:'320ms'}}/>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef}/>
              </div>

              {/* Input */}
              {limitReached ? (
                <div className="cb-limit-bar">
                  <p className="cb-limit-txt">Límite diario alcanzado · Se renueva en 24 h</p>
                </div>
              ) : (
                <form className="cb-input-wrap" onSubmit={sendMessage}>
                  <div className="cb-input-pill">
                    <input
                      className="cb-input"
                      type="text"
                      value={inputMessage}
                      onChange={e => setInputMessage(e.target.value)}
                      placeholder="Escribe tu mensaje..."
                      disabled={isLoading}
                      autoComplete="off"
                    />
                    <button className="cb-send" type="submit" disabled={isLoading || !inputMessage.trim()}>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                      </svg>
                    </button>
                  </div>
                </form>
              )}
            </>
          )}

          {/* Tabs */}
          <div className="cb-tabbar">
            <button type="button" className={`cb-tab${view === 'home' ? ' cb-tab--active' : ''}`} onClick={() => setView('home')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><path d="M9 22V12h6v10"/>
              </svg>
              <span>Inicio</span>
            </button>
            <button type="button" className={`cb-tab${view === 'messages' ? ' cb-tab--active' : ''}`} onClick={() => setView('messages')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
              </svg>
              <span>Mensajes</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
